import { OptUtil } from "../../modules/s25-opt/s25.opt.util";
import { Item } from "../../pojo/Item";
import { ListService } from "../../services/list.service";
import { ListStateService } from "../../services/list.state.service";
import { S25Const } from "../../util/s25-const";
import type { ListColumn, ListResponse } from "./list.table.types";
import type { ListType } from "./list.with.options.component";
import { ListConfig } from "./list.config";

export namespace S25ItemReservationListConfig {
    const RESULT_COUNT_SUFFIX = "Reservations";

    export const sharedBehavior: Pick<
        ListConfig,
        "fetchListData" | "optBeanRunTime" | "resultCountSuffix" | "formatRowCount"
    > = {
        fetchListData,
        optBeanRunTime: detailOptBeanRunTime,
        resultCountSuffix: RESULT_COUNT_SUFFIX,
        formatRowCount: ListConfig.defaultFormatRowCount(RESULT_COUNT_SUFFIX),
    };

    export const location = buildDetailConfig({
        caption: "Location Reservation List",
        compSubject: "rm_rsrv",
        columnPref: "25L_space_details_columns",
        loadOptBean: (refreshTable, optBeanRunTime) =>
            OptUtil.formLocationDetailsListBean(refreshTable, optBeanRunTime),
    });

    export const resource = buildDetailConfig({
        caption: "Resource Reservation List",
        compSubject: "rs_rsrv",
        columnPref: "25L_resource_details_columns",
        loadOptBean: (refreshTable, optBeanRunTime) =>
            OptUtil.formResourceDetailsListBean(refreshTable, optBeanRunTime),
    });

    export const organization = buildDetailConfig({
        caption: "Organization Reservation List",
        compSubject: "org_rsrv",
        columnPref: "25L_org_details_columns",
        loadOptBean: (refreshTable, optBeanRunTime) =>
            OptUtil.formOrganizationDetailsListBean(refreshTable, optBeanRunTime),
    });

    export const contact = buildDetailConfig({
        caption: "Contact Reservation List",
        compSubject: "event",
        loadOptBean: (refreshTable, optBeanRunTime) => OptUtil.formContactDetailsListBean(refreshTable, optBeanRunTime),
    });

    const configByItemTypeId: Partial<Record<Item.Ids, ListConfig>> = {
        [Item.Ids.Location]: location,
        [Item.Ids.Resource]: resource,
        [Item.Ids.Organization]: organization,
        [Item.Ids.Contact]: contact,
    };

    const LIST_ID_PREFIX: Partial<Record<Item.Ids, string>> = {
        [Item.Ids.Location]: "loc",
        [Item.Ids.Resource]: "res",
        [Item.Ids.Organization]: "org",
        [Item.Ids.Contact]: "cont",
    };

    type DetailOptBeanRunTime = {
        searchContext: string;
        itemId: number;
        itemTypeId: Item.Ids;
        date: string | Date | null;
    };

    type DetailConfigOptions = {
        caption: string;
        compSubject: string;
        columnPref?: string;
        loadOptBean: ListConfig["loadOptBean"];
    };

    function buildDetailConfig(options: DetailConfigOptions): ListConfig {
        const { caption, compSubject, columnPref, loadOptBean } = options;

        return {
            ...sharedBehavior,
            caption,
            listData: {
                compSubject,
                defaultSort: "event",
                defaultOrder: "asc",
            },
            errorMessageLabel: caption,
            listStateId: detailListId,
            mergeColumns: mergeListColumns,
            columnChooser: columnPref ? { listColumnPrefName: columnPref } : {},
            loadOptBean,
        };
    }

    function mergeListColumns(cols: ListColumn[], listStateId: string | undefined): ListColumn[] {
        if (!listStateId) {
            return cols;
        }

        if (!ListStateService.getListCols(listStateId)) {
            ListStateService.setListCols(listStateId, cols);
        }

        const cachedCols = ListStateService.getListCols(listStateId) ?? [];
        const cachedColMap = new Map<string, ListColumn>();
        for (const cachedCol of cachedCols) {
            const key = cachedCol.prefname || cachedCol.prefId;
            if (key) {
                cachedColMap.set(key, cachedCol);
            }
        }

        return cols.map((col) => {
            const key = col.prefname || col.prefId;
            const cachedCol = key ? cachedColMap.get(key) : undefined;
            return cachedCol ? { ...col, ...cachedCol } : col;
        });
    }

    async function fetchListData(context: ListConfig.FetchContext): Promise<ListResponse> {
        const { config, listType, optBean, tableQuery } = context;
        const listData = config.listData;
        if (!listData) {
            throw new Error(`List data config missing for ${config.caption}`);
        }

        const { sortColumn, sortOrder } = ListConfig.extractTableSort(tableQuery);
        const searchQuery = detailSearchQuery(listType);

        const params = ListService.scopeToDataRequest({
            modelBean: {
                ...(listData.compType ? { comptype: listData.compType } : {}),
                compsubject: listData.compSubject,
                optBean,
                ...(listType.kind === "reservation"
                    ? {
                          searchQuery,
                          itemId: listType.itemId,
                      }
                    : {}),
                sortCol: { sort: sortColumn, order: sortOrder },
                chosen: { page: tableQuery.page + 1 },
                itemsPerPage: tableQuery.pageSize,
                cacheId: tableQuery.forceRefresh ? 0 : tableQuery.cacheId,
                paginationCall: !tableQuery.forceRefresh && tableQuery.cacheId ? 1 : undefined,
            },
        });

        return (await ListService.getData(params, { abortSignal: context.abortSignal })) as ListResponse;
    }

    export function detailSearchQuery(listType: ListType): string | undefined {
        if (listType.kind !== "reservation") {
            return undefined;
        }

        const queryKey = S25Const.typeId2ItemIdB[listType.itemTypeId];
        return `&${queryKey}=${listType.itemId}`;
    }

    export function detailListId(listType: ListType): string | undefined {
        if (listType.kind !== "reservation") {
            return undefined;
        }

        const prefix = LIST_ID_PREFIX[listType.itemTypeId];
        if (!prefix) {
            throw new Error(`Unsupported detail reservation list item type ${listType.itemTypeId}`);
        }

        return `list_${prefix}_typeid_${listType.itemTypeId}_itemid${listType.itemId}`;
    }

    export function detailOptBeanRunTime(listType: ListType): DetailOptBeanRunTime | Record<string, never> {
        if (listType.kind !== "reservation") {
            return {};
        }

        const searchContext = detailSearchQuery(listType);
        if (!searchContext) {
            throw new Error(`Unsupported detail reservation list item type ${listType.itemTypeId}`);
        }

        return {
            searchContext,
            itemId: listType.itemId,
            itemTypeId: listType.itemTypeId,
            date: listType.startDate ?? null,
        };
    }

    export function forType(listType: ListType): ListConfig {
        if (listType.kind !== "reservation") {
            throw new Error(`Unsupported reservation list type kind ${listType.kind}`);
        }

        const config = configByItemTypeId[listType.itemTypeId];
        if (!config) {
            throw new Error(`Unsupported reservation list item type ${listType.itemTypeId}`);
        }

        return config;
    }
}
